import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";

const TYPE_OPTIONS = [
    { value: "", label: "All Levels" },
    { value: "land", label: "Land" },
    { value: "section", label: "Section" },
    { value: "field", label: "Field" },
    { value: "plant", label: "Plant" },
];

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "done", label: "Done" },
];

const statusLabel = (s) => (s === "done" ? "Done" : s === "pending" ? "Pending" : s || "-");

const MaintenanceTasksReport = () => {
    const [maintainCats, setMaintainCats] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/masterdropdown/listof_activemaintaincategories.php')
            .then((res) => { if (mounted) setMaintainCats(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="Maintenance Tasks Report"
            subtitle="Maintenance tasks across land, section, field and plant levels"
            breadcrumb={["Reports", "Maintenance"]}
            fetchUrl="/reports/maintenance/tasks.php"
            rowKey={(r) => `${r.type}-${r.main_id}`}
            filterDefs={[
                { type: "select", key: "type", label: "Level", col: 3, options: TYPE_OPTIONS },
                { type: "select", key: "mc_id", label: "Category", col: 5, options: maintainCats, showSearch: true },
                { type: "select", key: "status", label: "Status", col: 3, options: STATUS_OPTIONS },
                { type: "search", key: "search", label: "Search", col: 3, placeholder: "Title, description" },
                { type: "date", key: "dateRange", label: "Target Date", col: 4 },
            ]}
            columns={[
                { title: 'Target Date', dataIndex: 'target_date', key: 'target_date', width: 110, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.target_date).valueOf() - dayjs(b.target_date).valueOf() },
                { title: 'Level', dataIndex: 'type', key: 'type', width: 100 },
                { title: 'Reference', dataIndex: 'level_ref', key: 'level_ref', width: 160, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Category', dataIndex: 'mc_name', key: 'mc_name', width: 150 },
                { title: 'Title', dataIndex: 'main_title', key: 'main_title' },
                {
                    title: 'Status', dataIndex: 'main_status', key: 'main_status', width: 100,
                    render: (s) => {
                        const color = s === "done" ? "#52c41a" : "#faad14";
                        return <span style={{ color, fontWeight: 600 }}>{statusLabel(s)}</span>;
                    },
                },
                { title: 'Completed', dataIndex: 'complete_date', key: 'complete_date', width: 110, render: (d) => d ? dayjs(d).format("YYYY-MM-DD") : "-" },
                { title: 'Created By', dataIndex: 'creator_name', key: 'creator_name', width: 130 },
            ]}
            pdf={{
                title: "MAINTENANCE TASKS REPORT",
                subtitle: "Maintenance tasks across land, section, field and plant levels",
                columns: [
                    { header: "Target Date" }, { header: "Level" }, { header: "Reference" }, { header: "Category" },
                    { header: "Title" }, { header: "Status" }, { header: "Completed" }, { header: "Created By" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        dayjs(r.target_date).format("YYYY-MM-DD"),
                        r.type ? r.type.charAt(0).toUpperCase() + r.type.slice(1) : "-",
                        r.level_ref || "-",
                        r.mc_name || "-",
                        r.main_title || "-",
                        statusLabel(r.main_status),
                        r.complete_date ? dayjs(r.complete_date).format("YYYY-MM-DD") : "-",
                        r.creator_name || "-",
                    ]),
            }}
            excel={{
                filename: "maintenance-tasks-report",
                sheetName: "Maintenance Tasks",
                columns: [
                    { title: "Target Date", dataIndex: "target_date", width: 12 },
                    { title: "Level", dataIndex: "type", width: 10 },
                    { title: "Reference", dataIndex: "level_ref", width: 20 },
                    { title: "Category", dataIndex: "mc_name", width: 20 },
                    { title: "Title", dataIndex: "main_title", width: 40 },
                    { title: "Status", dataIndex: "main_status", width: 10 },
                    { title: "Completed", dataIndex: "complete_date", width: 12 },
                    { title: "Created By", dataIndex: "creator_name", width: 18 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        type: r.type ? r.type.charAt(0).toUpperCase() + r.type.slice(1) : "",
                        target_date: dayjs(r.target_date).format("YYYY-MM-DD"),
                        complete_date: r.complete_date ? dayjs(r.complete_date).format("YYYY-MM-DD") : "",
                    })),
            }}
        />
    );
};

export default MaintenanceTasksReport;
